import React, {useEffect, useState} from "react";
import {useTranslation} from "next-i18next";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {
  fetchSubscriptions,
  resetCheckoutSession,
  selectCheckoutSessionUrl,
  selectCreateSessionError
} from "../../redux/upgrade/subscriptionSlice";
import {useRouter} from "next/router";
import {Hero, HeroBody} from "../text";
import {UpgradeLandingHeaderSectionView} from "./UpgradeLandingHeaderSectionView";
import {UpgradeLandingFeaturesSectionView} from "./UpgradeLandingFeaturesSectionView";
import {LandingTransformationsView} from "../landing/LandingTransformationsView";
import {Container} from "../containers/Container";
import {UpgradeSubscriptionOptionsView} from "./UpgradeSubscriptionOptionsView";
import {Button} from "../buttons/Button";
import {FaqItem, LandingFaqSectionView} from "../landing/LandingFaqSectionView";
import Link from "next/link"
import {AlertDialog, AlertDialogButtonProps} from "../dialogs/AlertDialog";
import {ApiError} from "../../errors/apiError";
import {RealmServiceError} from "../../errors/realmServiceError";
import {useModalRouting} from "../../utils/useModalRouting";

export const UpgradeLandingView: React.FC<any> = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const router = useRouter()
  const { createRoute  } = useModalRouting()

  const [errorButtons, setErrorButtons] = useState<AlertDialogButtonProps[]>([])

  const checkoutSessionUrl = useAppSelector(selectCheckoutSessionUrl)
  const createSessionError = useAppSelector(selectCreateSessionError)

  useEffect(() => {
    if (checkoutSessionUrl) {
      window.location.href = checkoutSessionUrl
      dispatch(resetCheckoutSession())
    }
  }, [checkoutSessionUrl])

  useEffect(() => {
    if (createSessionError instanceof ApiError && createSessionError.code === 'UNAUTHORIZED' || createSessionError instanceof RealmServiceError && createSessionError.code === 'NOT_LOGGED_IN') {
      setErrorButtons([
        {
          title: { key: 'common:common_title_ok' },
          onClick: () => {
            router.push(
              createRoute({ name: 'login', query: { config: 'sign_up' } }),
              undefined,
              { shallow: true }
            );
          },
          style: 'default'
        }
      ])
    } else {
      setErrorButtons([
        {
          title: { key: 'common:common_title_ok' },
          onClick: () => {
            dispatch(resetCheckoutSession())
          },
          style: 'default'
        }
      ])
    }
  }, [createSessionError, dispatch])

  const faqItems: FaqItem[] = [
    {
      question: 'What do I get with premium?',
      answer: 'Curated meal plans & recipes, carb/macro cycling, tracking for your vitamin & mineral goals, your very own, customizable food database, premium customer support, and so much more!',
      id: 'premium'
    },
    {
      question: 'Can I cancel my membership anytime?',
      answer: 'You can cancel anytime and will continue to have premium access until the period you paid for is over. To cancel, login to your account and click "Cancel Subscription".',
      id: 'cancel'
    },
    {
      question: 'How does the diet coach work?',
      answer: 'Every week, our algorithm will analyze your logged nutrition, weight, and activity then optimize your plan if needed.',
      id: 'diet_coach'
    }
  ]

  useEffect(() => {
    const code: string | undefined = router.query.code as string
    dispatch(fetchSubscriptions(code))
  }, [router.query.code, dispatch])

  return <>
    <AlertDialog
      isOpen={!!createSessionError}
      error={createSessionError}
      onClose={() => dispatch(resetCheckoutSession())}
      isDismissable={false}
      buttons={errorButtons}
    />

    <div>
      <UpgradeLandingHeaderSectionView />
    </div>

    <div className="mt-8 lg:mt-0">
      <UpgradeLandingFeaturesSectionView />
    </div>

    <Container className="mt-8 lg:mt-0 flex flex-col space-y-12 lg:space-y-16 xl:space-y-20">
      <Hero className="text-center">
        Premium <span className="text-fg-primary font-bold">Success Stories</span>
      </Hero>

      <LandingTransformationsView />
    </Container>

    <Container className="mt-8 lg:mt-0 w-full flex flex-col items-center space-y-12 lg:space-y-16 xl:space-y-20">
      <Hero className="text-center">
        Start <span className="text-fg-primary font-bold">your own transformation.</span>
      </Hero>

      <UpgradeSubscriptionOptionsView />
    </Container>

    <Container className="mt-8 lg:mt-8 flex flex-col items-center space-y-6 lg:space-y-8 xl:space-y-12">
      <HeroBody className="text-center">
        Not ready to upgrade? Try the basic version for free.
      </HeroBody>

      <div className="flex flex-col items-center">
        <div className="w-80">
          <Link href={`/download`}>
            <a>
              <Button isFullWidth size="xl" buttonStyle="secondary">Get Basic Version</Button>
            </a>
          </Link>
        </div>
      </div>
    </Container>

    <Container className="mt-8 lg:mt-8 flex flex-col items-center space-y-12 lg:space-y-16 xl:space-y-20 mb-12 lg:mb-16 xl:mb-20">
      <Hero className="text-center">
        Common Questions
      </Hero>

      <LandingFaqSectionView items={faqItems} />
    </Container>
  </>
}